import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  X,
  Building2,
  UtensilsCrossed,
  Music,
  Camera,
  Sparkles,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Eye,
  Settings2,
  ImagePlus,
  Loader2,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';

// ──────────────────────────────────────────────────────────────────────
// B-EXP-6.10 — Concierge Brief with persistence + photo attachments.
//
// Persisted to backend:
//   GET  /api/admin/concierge/workspace/{event_id}/brief-draft
//   PUT  /api/admin/concierge/workspace/{event_id}/brief-draft
//
// Photo upload reuses the existing image-upload service:
//   POST /api/upload/brief-photo  →  { url }
//
// Rec shape:  { id, name, tier, reason, photos: [url, ...] }
// ──────────────────────────────────────────────────────────────────────

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREEN = '#0F5132';
const PURPLE = '#5B47E8';
const MAX_PHOTOS_PER_REC = 8;
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024; // matches backend MAX_FILE_SIZE
const ALLOWED_MIME = new Set(['image/jpeg', 'image/jpg', 'image/png']);

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const resolveImgUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
};

const SLOTS = [
  {
    key: 'venue',
    label: 'Venue',
    description: 'Find the perfect space for their event.',
    placeholder: 'e.g. Vista Hall',
    icon: Building2,
    tint: { bg: '#FDE4DC', fg: '#B4321C', pill: 'bg-[#FDE4DC] text-[#B4321C]' },
  },
  {
    key: 'food',
    label: 'Food & Catering',
    description: 'Catering, desserts, and menu options.',
    placeholder: 'e.g. Coastal Catering Co.',
    icon: UtensilsCrossed,
    tint: { bg: '#FDEFC5', fg: '#8B5A0F', pill: 'bg-[#FDEFC5] text-[#8B5A0F]' },
  },
  {
    key: 'entertainment',
    label: 'Entertainment',
    description: 'DJ, live music, performers & more.',
    placeholder: 'e.g. Sundown String Trio',
    icon: Music,
    tint: { bg: '#EBE6FF', fg: '#4C36C7', pill: 'bg-[#EBE6FF] text-[#4C36C7]' },
  },
  {
    key: 'photo',
    label: 'Photo & Video',
    description: 'Capture every special moment.',
    placeholder: 'e.g. Bright Frame Studios',
    icon: Camera,
    tint: { bg: '#FCE0EE', fg: '#B4297A', pill: 'bg-[#FCE0EE] text-[#B4297A]' },
  },
  {
    key: 'decor',
    label: 'Décor & Rentals',
    description: 'Design, décor, and rentals to wow.',
    placeholder: 'e.g. Bloom & Co',
    icon: Sparkles,
    tint: { bg: '#DFF1E1', fg: '#146C43', pill: 'bg-[#DFF1E1] text-[#146C43]' },
  },
];

const TIERS = ['$', '$$', '$$$'];

const emptyDraft = () => ({
  recommendations: SLOTS.reduce((acc, s) => ({ ...acc, [s.key]: [] }), {}),
  additional_notes: '',
});

// ── Photo picker sub-component ─────────────────────────────────────────
const PhotoPicker = ({ slotKey, photos, onChange, disabled }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList).slice(0, MAX_PHOTOS_PER_REC - photos.length);
    if (files.length === 0) {
      toast.error(`Max ${MAX_PHOTOS_PER_REC} photos per recommendation.`);
      return;
    }
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      if (!ALLOWED_MIME.has(file.type)) {
        toast.error(`${file.name}: only JPEG or PNG.`);
        continue;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        toast.error(`${file.name}: exceeds 5MB.`);
        continue;
      }
      try {
        const form = new FormData();
        form.append('file', file);
        const res = await axios.post(`${API}/upload/brief-photo`, form, {
          headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
        });
        if (res.data?.url) uploaded.push(res.data.url);
      } catch (e) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    if (uploaded.length > 0) {
      onChange([...photos, ...uploaded]);
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeAt = (idx) => {
    const next = photos.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div className="space-y-2" data-testid={`concierge-playbook-form-${slotKey}-photos`}>
      <Label className="text-[11px] uppercase tracking-[0.12em]" style={{ color: GREEN }}>
        Photos
      </Label>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/jpg"
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
        data-testid={`concierge-playbook-form-${slotKey}-photo-input`}
      />
      <div className="flex flex-wrap gap-2">
        {photos.map((url, idx) => (
          <div
            key={`${url}-${idx}`}
            className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#E8DEC7] bg-[#FAF6EE]"
            data-testid={`concierge-playbook-form-${slotKey}-photo-thumb-${idx}`}
          >
            <img
              src={resolveImgUrl(url)}
              alt=""
              className="w-full h-full object-contain"
              loading="lazy"
            />
            {!disabled ? (
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black/90"
                aria-label="Remove photo"
                data-testid={`concierge-playbook-form-${slotKey}-photo-remove-${idx}`}
              >
                <X className="w-3 h-3" />
              </button>
            ) : null}
          </div>
        ))}
        {photos.length < MAX_PHOTOS_PER_REC && !disabled ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className={`w-20 h-20 rounded-lg border-2 border-dashed border-[#DFD5FF] text-[#5B47E8] flex flex-col items-center justify-center gap-1 hover:bg-[#F3EFFF] transition-colors ${uploading ? 'opacity-70 cursor-wait' : ''}`}
            data-testid={`concierge-playbook-form-${slotKey}-photo-add-btn`}
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
            <span className="text-[10px] font-medium">{uploading ? 'Uploading' : 'Add photo'}</span>
          </button>
        ) : null}
      </div>
      <p className="text-[10.5px] text-gray-400">
        JPEG or PNG, up to 5MB each. {photos.length} / {MAX_PHOTOS_PER_REC} used.
      </p>
    </div>
  );
};

// ── Inline form for adding/editing a recommendation ────────────────────
const RecommendationForm = ({ slot, initial, onSave, onCancel }) => {
  const [name, setName] = useState(initial?.name || '');
  const [tier, setTier] = useState(initial?.tier || '');
  const [reason, setReason] = useState(initial?.reason || '');
  const [photos, setPhotos] = useState(initial?.photos || []);
  // B-EXP-6.11 — extended optional customer-facing fields.
  const [location, setLocation] = useState(initial?.location || '');
  const [priceDisplay, setPriceDisplay] = useState(initial?.price_display || '');
  const [subtitle, setSubtitle] = useState(initial?.subtitle || '');
  const [whySelected, setWhySelected] = useState(initial?.why_selected || '');
  const [insight, setInsight] = useState(initial?.insight || '');
  const [highlightsText, setHighlightsText] = useState(
    Array.isArray(initial?.highlights) ? initial.highlights.join('\n') : ''
  );
  const [externalUrl, setExternalUrl] = useState(initial?.external_url || '');
  const hasExtras =
    !!(initial?.location || initial?.price_display || initial?.subtitle ||
       initial?.why_selected || initial?.insight ||
       (initial?.highlights || []).length || initial?.external_url);
  const [expandedExtras, setExpandedExtras] = useState(hasExtras);

  const canSave = name.trim().length > 0;

  return (
    <div
      className="rounded-xl bg-[#FAF6EE] border border-[#EFE7D4] p-4 space-y-3"
      data-testid={`concierge-playbook-form-${slot.key}`}
    >
      <div>
        <Label className="text-[11px] uppercase tracking-[0.12em]" style={{ color: GREEN }}>Name</Label>
        <Input
          placeholder={slot.placeholder}
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid={`concierge-playbook-form-${slot.key}-name`}
          className="mt-1.5 border-[#E8DEC7] bg-white text-gray-900"
        />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-[0.12em]" style={{ color: GREEN }}>Price tier (internal)</Label>
        <div className="flex gap-2 mt-1.5">
          {TIERS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTier(tier === t ? '' : t)}
              className={`text-sm px-3.5 py-1 rounded-full border transition-colors ${
                tier === t
                  ? 'text-white border-transparent'
                  : 'bg-white text-gray-600 border-[#E8DEC7] hover:border-[#5B47E8]'
              }`}
              style={tier === t ? { backgroundColor: PURPLE } : undefined}
              data-testid={`concierge-playbook-form-${slot.key}-tier-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-[0.12em]" style={{ color: GREEN }}>One-line reason we love it (optional)</Label>
        <Textarea
          rows={2}
          maxLength={240}
          placeholder="e.g. Pet-friendly, $2.8k for 30 guests, sunset balcony."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid={`concierge-playbook-form-${slot.key}-reason`}
          className="mt-1.5 border-[#E8DEC7] bg-white text-gray-900"
        />
      </div>
      <PhotoPicker
        slotKey={slot.key}
        photos={photos}
        onChange={setPhotos}
      />

      {/* Additional details — progressive disclosure */}
      <div className="pt-1">
        <button
          type="button"
          onClick={() => setExpandedExtras((v) => !v)}
          className="inline-flex items-center gap-1.5 text-[12px] font-medium hover:opacity-80"
          style={{ color: PURPLE }}
          data-testid={`concierge-playbook-form-${slot.key}-toggle-extras`}
          aria-expanded={expandedExtras}
        >
          {expandedExtras ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          Additional details for the customer brief
        </button>
      </div>

      {expandedExtras ? (
        <div className="space-y-3 pt-1" data-testid={`concierge-playbook-form-${slot.key}-extras`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em]" style={{ color: GREEN }}>Location</Label>
              <Input
                placeholder="e.g. Slidell, Louisiana"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                data-testid={`concierge-playbook-form-${slot.key}-location`}
                className="mt-1.5 border-[#E8DEC7] bg-white text-gray-900"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em]" style={{ color: GREEN }}>Customer-facing price display</Label>
              <Input
                placeholder="e.g. $750–$3,500 venue packages"
                value={priceDisplay}
                onChange={(e) => setPriceDisplay(e.target.value)}
                data-testid={`concierge-playbook-form-${slot.key}-price-display`}
                className="mt-1.5 border-[#E8DEC7] bg-white text-gray-900"
              />
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.12em]" style={{ color: GREEN }}>Positioning statement / subtitle</Label>
            <Input
              placeholder="e.g. The strongest match for a venue that can handle most of the celebration in one place."
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              data-testid={`concierge-playbook-form-${slot.key}-subtitle`}
              className="mt-1.5 border-[#E8DEC7] bg-white text-gray-900"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.12em]" style={{ color: GREEN }}>Why we selected this</Label>
            <Textarea
              rows={3}
              maxLength={1200}
              placeholder="A short editorial paragraph explaining how this fits the customer's event."
              value={whySelected}
              onChange={(e) => setWhySelected(e.target.value)}
              data-testid={`concierge-playbook-form-${slot.key}-why-selected`}
              className="mt-1.5 border-[#E8DEC7] bg-white text-gray-900"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.12em]" style={{ color: GREEN }}>Concierge Insight (optional)</Label>
            <Textarea
              rows={2}
              maxLength={1200}
              placeholder="e.g. We spoke with the owner; she's holding this date until Friday."
              value={insight}
              onChange={(e) => setInsight(e.target.value)}
              data-testid={`concierge-playbook-form-${slot.key}-insight`}
              className="mt-1.5 border-[#E8DEC7] bg-white text-gray-900"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.12em]" style={{ color: GREEN }}>Highlights / includes (one per line, up to 10)</Label>
            <Textarea
              rows={4}
              placeholder={"On-site catering coordinator\nBar package included\nRooftop terrace"}
              value={highlightsText}
              onChange={(e) => setHighlightsText(e.target.value)}
              data-testid={`concierge-playbook-form-${slot.key}-highlights`}
              className="mt-1.5 border-[#E8DEC7] bg-white text-gray-900"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.12em]" style={{ color: GREEN }}>External details URL (optional)</Label>
            <Input
              placeholder="https://…"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              data-testid={`concierge-playbook-form-${slot.key}-external-url`}
              className="mt-1.5 border-[#E8DEC7] bg-white text-gray-900"
            />
          </div>
        </div>
      ) : null}

      <div className="flex gap-2 justify-end pt-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          data-testid={`concierge-playbook-form-${slot.key}-cancel-btn`}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!canSave}
          onClick={() => onSave({
            name: name.trim(),
            tier: tier || null,
            reason: reason.trim() || null,
            photos,
            location: location.trim() || null,
            price_display: priceDisplay.trim() || null,
            subtitle: subtitle.trim() || null,
            why_selected: whySelected.trim() || null,
            insight: insight.trim() || null,
            highlights: highlightsText
              .split('\n')
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
              .slice(0, 10),
            external_url: externalUrl.trim() || null,
          })}
          style={canSave ? { backgroundColor: PURPLE, color: 'white' } : undefined}
          data-testid={`concierge-playbook-form-${slot.key}-save-btn`}
        >
          {initial ? 'Save changes' : 'Add to brief'}
        </Button>
      </div>
    </div>
  );
};

// ── Single saved recommendation row (shown when expanded) ──────────────
const RecommendationRow = ({ slot, rec, onEdit, onRemove, onMoveUp, onMoveDown, canMoveUp, canMoveDown }) => (
  <div
    className="group flex items-start justify-between gap-3 py-2.5 px-3 rounded-lg hover:bg-[#FAF6EE] transition-colors"
    data-testid={`concierge-playbook-rec-${slot.key}-${rec.id}`}
  >
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-[14px] font-medium text-gray-900">{rec.name}</span>
        {rec.tier ? <span className="text-[11px] text-gray-400 tracking-wide">{rec.tier}</span> : null}
        {rec.location ? (
          <span className="text-[11px] text-gray-500" data-testid={`concierge-playbook-rec-${slot.key}-${rec.id}-location`}>
            · {rec.location}
          </span>
        ) : null}
        {rec.photos && rec.photos.length > 0 ? (
          <span className="text-[11px] text-gray-400" data-testid={`concierge-playbook-rec-${slot.key}-${rec.id}-photo-count`}>
            · {rec.photos.length} photo{rec.photos.length === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>
      {rec.subtitle ? (
        <p className="text-[12px] text-gray-700 mt-0.5 leading-relaxed italic">{rec.subtitle}</p>
      ) : rec.reason ? (
        <p className="text-[12px] text-gray-600 mt-0.5 leading-relaxed">{rec.reason}</p>
      ) : null}
      {rec.photos && rec.photos.length > 0 ? (
        <div className="flex gap-1.5 mt-2">
          {rec.photos.slice(0, 4).map((url, idx) => (
            <div
              key={`${url}-${idx}`}
              className="w-12 h-12 rounded-md overflow-hidden border border-[#EFE7D4] bg-[#FAF6EE]"
            >
              <img src={resolveImgUrl(url)} alt="" className="w-full h-full object-contain" loading="lazy" />
            </div>
          ))}
          {rec.photos.length > 4 ? (
            <div className="w-12 h-12 rounded-md bg-[#FAF6EE] border border-[#EFE7D4] flex items-center justify-center text-[10px] text-gray-500 font-medium">
              +{rec.photos.length - 4}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
    <div className="flex-none flex gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
      <button
        type="button"
        onClick={onMoveUp}
        disabled={!canMoveUp}
        className="p-1.5 rounded-md text-gray-400 hover:text-[#5B47E8] hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        aria-label="Move up"
        data-testid={`concierge-playbook-rec-${slot.key}-${rec.id}-move-up-btn`}
      >
        <ChevronUp className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={onMoveDown}
        disabled={!canMoveDown}
        className="p-1.5 rounded-md text-gray-400 hover:text-[#5B47E8] hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        aria-label="Move down"
        data-testid={`concierge-playbook-rec-${slot.key}-${rec.id}-move-down-btn`}
      >
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={onEdit}
        className="p-1.5 rounded-md text-gray-400 hover:text-[#5B47E8] hover:bg-white"
        aria-label="Edit recommendation"
        data-testid={`concierge-playbook-rec-${slot.key}-${rec.id}-edit-btn`}
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="p-1.5 rounded-md text-gray-400 hover:text-rose-700 hover:bg-white"
        aria-label="Remove recommendation"
        data-testid={`concierge-playbook-rec-${slot.key}-${rec.id}-remove-btn`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  </div>
);

// ── Category row (compact) — click to expand and manage recs ───────────
const CategoryRow = ({ slot, recs, expanded, onToggle, editing, openFormId, onAdd, onSave, onRemove, onMove, setOpenFormId }) => {
  const Icon = slot.icon;
  const count = recs.length;
  const formOpen = openFormId === slot.key;
  // Aggregate first two photos across all saved recs for the collapsed preview
  const previewPhotos = [];
  for (const r of recs) {
    for (const p of (r.photos || [])) {
      previewPhotos.push(p);
      if (previewPhotos.length >= 2) break;
    }
    if (previewPhotos.length >= 2) break;
  }

  return (
    <div
      className={`rounded-xl border transition-all ${
        expanded ? 'border-[#E8DEC7] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.03)]' : 'border-[#EFE7D4] bg-white hover:border-[#DFD3B8]'
      }`}
      data-testid={`concierge-playbook-section-${slot.key}`}
    >
      {/* Collapsed header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-3 text-left"
        data-testid={`concierge-playbook-category-toggle-${slot.key}`}
        aria-expanded={expanded}
      >
        <div
          className="flex-none w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: slot.tint.bg }}
          aria-hidden
        >
          <Icon className="w-5 h-5" style={{ color: slot.tint.fg }} />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-[16px] font-semibold" style={{ color: GREEN }}>{slot.label}</h3>
          <p className="text-[12.5px] text-gray-500 mt-0.5">{slot.description}</p>
          <div className="mt-1.5">
            <span
              className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-md font-medium ${slot.tint.pill}`}
              data-testid={`concierge-playbook-count-${slot.key}`}
            >
              {count === 0 ? 'No options yet' : `${count} option${count === 1 ? '' : 's'} added`}
            </span>
          </div>
        </div>

        {previewPhotos.length > 0 ? (
          <div className="hidden sm:flex gap-2 flex-none" data-testid={`concierge-playbook-preview-${slot.key}`}>
            {previewPhotos.map((url, idx) => (
              <div
                key={`${url}-${idx}`}
                className="w-16 h-14 rounded-lg overflow-hidden border border-[#EFE7D4] bg-[#FAF6EE]"
              >
                <img src={resolveImgUrl(url)} alt="" className="w-full h-full object-contain" loading="lazy" />
              </div>
            ))}
          </div>
        ) : null}

        <div className="flex-none text-[#8B7B5A]">
          {expanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </div>
      </button>

      {/* Expanded */}
      {expanded ? (
        <div className="px-3 pb-3 pt-1 space-y-1 border-t border-[#F3EBD8]">
          {recs.map((rec, recIdx) => (
            editing?.slot === slot.key && editing?.id === rec.id ? (
              <div key={rec.id} className="pt-3">
                <RecommendationForm
                  slot={slot}
                  initial={rec}
                  onSave={(data) => onSave(slot.key, data, rec.id)}
                  onCancel={() => setOpenFormId(null)}
                />
              </div>
            ) : (
              <RecommendationRow
                key={rec.id}
                slot={slot}
                rec={rec}
                onEdit={() => onAdd(slot.key, rec.id)}
                onRemove={() => onRemove(slot.key, rec.id)}
                onMoveUp={() => onMove(slot.key, recIdx, -1)}
                onMoveDown={() => onMove(slot.key, recIdx, 1)}
                canMoveUp={recIdx > 0}
                canMoveDown={recIdx < recs.length - 1}
              />
            )
          ))}
          {formOpen && !editing ? (
            <div className="pt-3">
              <RecommendationForm
                slot={slot}
                initial={null}
                onSave={(data) => onSave(slot.key, data, null)}
                onCancel={() => setOpenFormId(null)}
              />
            </div>
          ) : null}
          {!formOpen ? (
            <button
              type="button"
              onClick={() => onAdd(slot.key, null)}
              className="w-full text-left py-2 px-3 mt-1 flex items-center gap-2 text-[13px] font-medium rounded-lg transition-colors"
              style={{ color: PURPLE }}
              data-testid={`concierge-playbook-add-${slot.key}-btn`}
            >
              <Plus className="w-4 h-4" aria-hidden />
              Add {slot.label.toLowerCase()} option
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

// ── The main brief ─────────────────────────────────────────────────────
export default function ConciergePlaybook({ eventId, onDraftSaved }) {
  const [draft, setDraft] = useState(emptyDraft);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openFormId, setOpenFormId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const notesTimer = useRef(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const handleViewAsCustomer = async () => {
    if (!eventId || previewLoading) return;
    setPreviewLoading(true);
    // Pre-open a placeholder tab so the blob open happens synchronously with
    // the user gesture (avoids popup blockers on Safari/Firefox).
    const previewWindow = window.open('', '_blank');
    if (previewWindow) {
      previewWindow.document.write(
        '<!DOCTYPE html><html><head><title>Loading preview…</title>' +
        '<style>body{font-family:system-ui;background:#FFF7EE;color:#6B6558;' +
        'display:flex;align-items:center;justify-content:center;height:100vh;' +
        'margin:0;font-size:14px}</style></head><body>Preparing preview…</body></html>'
      );
    }
    try {
      // Force-save latest state before syncing (in case notes are still
      // pending debounce).
      if (notesTimer.current) {
        clearTimeout(notesTimer.current);
        notesTimer.current = null;
        await persist(draft);
      }
      // Fetch preview HTML with auth; open as blob URL.
      const res = await axios.get(
        `${API}/admin/concierge/workspace/${eventId}/brief-preview`,
        { headers: authHeaders(), responseType: 'text', transformResponse: [(d) => d] },
      );
      const blob = new Blob([res.data], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      if (previewWindow && !previewWindow.closed) {
        previewWindow.location.replace(url);
      } else {
        window.open(url, '_blank');
      }
      // Revoke after a delay so the tab has time to load.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (previewWindow && !previewWindow.closed) {
        try { previewWindow.close(); } catch (_) { /* noop */ }
      }
      toast.error("Couldn't open the customer preview.");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Load persisted draft.
  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/admin/concierge/workspace/${eventId}/brief-draft`,
          { headers: authHeaders() },
        );
        if (cancelled) return;
        const server = res.data || {};
        const recs = server.recommendations || {};
        const normalized = SLOTS.reduce(
          (acc, s) => ({ ...acc, [s.key]: Array.isArray(recs[s.key]) ? recs[s.key] : [] }),
          {},
        );
        setDraft({
          recommendations: normalized,
          additional_notes: server.additional_notes || '',
        });
      } catch (_) {
        // Best-effort; render empty state.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  const persist = useCallback(
    async (nextDraft) => {
      if (!eventId) return;
      setSaving(true);
      try {
        await axios.put(
          `${API}/admin/concierge/workspace/${eventId}/brief-draft`,
          nextDraft,
          { headers: authHeaders() },
        );
        // B-EXP-6.28 — a saved draft change may flip publish status to
        // "Republish updates"; let the workspace re-fetch the source of truth.
        if (onDraftSaved) onDraftSaved();
      } catch (_) {
        toast.error("Couldn't save the brief. Try again.");
      } finally {
        setSaving(false);
      }
    },
    [eventId, onDraftSaved],
  );

  const toggleExpanded = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleAdd = (slotKey, recId) => {
    setExpanded((prev) => new Set(prev).add(slotKey));
    if (recId) {
      setEditing({ slot: slotKey, id: recId });
      setOpenFormId(slotKey);
    } else {
      setEditing(null);
      setOpenFormId(slotKey);
    }
  };

  const handleSaveRec = (slotKey, data, recId) => {
    const list = draft.recommendations[slotKey] || [];
    let nextList;
    if (recId) {
      nextList = list.map((r) => (r.id === recId ? { ...r, ...data } : r));
    } else {
      // Prefer crypto.randomUUID for collision-proof rec IDs; fall back
      // to the legacy `${slotKey}-${Date.now()}` shape on ancient browsers.
      const newId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${slotKey}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const newRec = { id: newId, ...data };
      nextList = [...list, newRec];
    }
    const nextDraft = {
      ...draft,
      recommendations: { ...draft.recommendations, [slotKey]: nextList },
    };
    setDraft(nextDraft);
    setOpenFormId(null);
    setEditing(null);
    persist(nextDraft);
  };

  const handleRemoveRec = (slotKey, recId) => {
    const nextList = (draft.recommendations[slotKey] || []).filter((r) => r.id !== recId);
    const nextDraft = {
      ...draft,
      recommendations: { ...draft.recommendations, [slotKey]: nextList },
    };
    setDraft(nextDraft);
    persist(nextDraft);
  };

  const handleMoveRec = (slotKey, index, delta) => {
    const list = draft.recommendations[slotKey] || [];
    const target = index + delta;
    if (target < 0 || target >= list.length) return;
    const nextList = list.slice();
    const [moved] = nextList.splice(index, 1);
    nextList.splice(target, 0, moved);
    const nextDraft = {
      ...draft,
      recommendations: { ...draft.recommendations, [slotKey]: nextList },
    };
    setDraft(nextDraft);
    persist(nextDraft);
  };

  const handleNotesChange = (value) => {
    const nextDraft = { ...draft, additional_notes: value };
    setDraft(nextDraft);
    if (notesTimer.current) clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(() => persist(nextDraft), 800);
  };

  const totalRecs = useMemo(
    () => Object.values(draft.recommendations).reduce((n, arr) => n + arr.length, 0),
    [draft.recommendations],
  );

  return (
    <article
      className="bg-white rounded-2xl border border-[#EFE7D4] shadow-[0_1px_2px_rgba(15,23,42,0.03)] overflow-hidden"
      data-testid="concierge-playbook-section"
    >
      <div className="px-6 sm:px-8 py-6 sm:py-7 space-y-6">
        {/* Header */}
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <h2
              className="text-[13px] font-semibold uppercase tracking-[0.14em]"
              style={{ color: GREEN }}
              data-testid="concierge-brief-heading"
            >
              Concierge Brief
            </h2>
            <span
              className="text-[11px] font-medium px-2.5 py-0.5 rounded-md"
              style={{ backgroundColor: '#EBE6FF', color: PURPLE }}
              data-testid="concierge-brief-state-pill"
            >
              Drafting
            </span>
            {saving ? (
              <span className="text-[11px] text-gray-400 inline-flex items-center gap-1" data-testid="concierge-brief-saving">
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving…
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled
              aria-disabled="true"
              title="Wired in a later step"
              className="gap-1.5 border-[#E8DEC7] text-gray-700 hover:bg-[#FAF6EE] opacity-80 cursor-not-allowed"
              data-testid="concierge-playbook-brief-settings"
            >
              <Settings2 className="w-4 h-4" aria-hidden />
              Brief settings
            </Button>
            <Button
              size="sm"
              disabled={previewLoading}
              title="Open the customer-facing brief in a new tab"
              className="gap-1.5 text-white border-transparent hover:opacity-90"
              style={{ backgroundColor: PURPLE }}
              onClick={handleViewAsCustomer}
              data-testid="concierge-playbook-view-as-customer"
            >
              {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : <Eye className="w-4 h-4" aria-hidden />}
              {previewLoading ? 'Opening…' : 'View as customer'}
            </Button>
          </div>
        </header>

        <p className="text-[13.5px] italic text-gray-600 leading-relaxed" data-testid="concierge-brief-helper">
          Build the brief below. Your customer will see exactly what you send.
        </p>

        <div className="border-t border-[#F0E7D5]" />

        {/* Recommendations header */}
        <div className="flex items-center justify-between gap-4">
          <h3
            className="text-[13px] font-semibold uppercase tracking-[0.14em]"
            style={{ color: GREEN }}
          >
            Recommendations
          </h3>
          <button
            type="button"
            onClick={() => {
              const firstEmpty = SLOTS.find((s) => (draft.recommendations[s.key] || []).length === 0) || SLOTS[0];
              handleAdd(firstEmpty.key, null);
            }}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium"
            style={{ color: PURPLE }}
            data-testid="concierge-playbook-add-recommendation-btn"
          >
            <Plus className="w-4 h-4" aria-hidden />
            Add recommendation
          </button>
        </div>

        {/* Category rows */}
        <div className="space-y-3">
          {SLOTS.map((slot) => (
            <CategoryRow
              key={slot.key}
              slot={slot}
              recs={draft.recommendations[slot.key] || []}
              expanded={expanded.has(slot.key)}
              onToggle={() => toggleExpanded(slot.key)}
              editing={editing?.slot === slot.key ? editing : null}
              openFormId={openFormId}
              onAdd={handleAdd}
              onSave={handleSaveRec}
              onRemove={handleRemoveRec}
              onMove={handleMoveRec}
              setOpenFormId={setOpenFormId}
            />
          ))}
        </div>

        {/* Additional notes for customer */}
        <div className="border-t border-[#F0E7D5] pt-6 space-y-3">
          <h3
            className="text-[13px] font-semibold uppercase tracking-[0.14em]"
            style={{ color: GREEN }}
          >
            Additional notes for customer
          </h3>
          <Textarea
            rows={4}
            maxLength={1000}
            placeholder="Add any additional notes, disclaimers, or helpful information for your customer…"
            value={draft.additional_notes}
            onChange={(e) => handleNotesChange(e.target.value)}
            data-testid="concierge-playbook-note-textarea"
            className="resize-none border-[#E8DEC7] bg-[#FCFBF7] text-[14px] leading-relaxed text-gray-800 placeholder:text-gray-400 focus-visible:ring-1 focus-visible:ring-[#5B47E8]"
          />
          <p className="text-[10px] text-gray-400 text-right tracking-wide">{draft.additional_notes.length} / 1000</p>
        </div>

        {/* Silent counters for tests */}
        <div className="sr-only" aria-hidden data-testid="concierge-playbook-total-recs">{totalRecs}</div>
        <div className="sr-only" aria-hidden data-testid="concierge-playbook-loaded">{loaded ? 'yes' : 'no'}</div>
      </div>
    </article>
  );
}
